import { UI } from "@wwimmo/ui";
import { useCallback, useState, useContext, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import { observer } from "mobx-react";
import * as Sentry from "@sentry/react";
import { IInvoiceFile } from "src/stores/krediflow/InvoiceTypes";
import styles from "./InvoiceFilePreview.module.css";
import { Document, Page, pdfjs } from "react-pdf";
import "./AnnotationLayer.css";
import "./TextLayer.css";
import { NetworkConfig } from "src/network/NetworkConfig";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { debounce } from "lodash";
import { FileSelectorItem } from "src/components/fileSelector/FileSelectorItem";
import { FileSelectorContainer } from "src/components/fileSelector/FileSelectorContainer";
import { FILE_TYPE } from "src/enums";
import { INVOICE_PROPERTY } from "src/stores/krediflow/InvoiceEnums";
import { IFile } from "src/types";
import { SCREEN_SIZE } from "src/stores/UIStore";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const pdfDocumentOptions = {
    wasmUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/wasm/`,
    cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
    standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/standard_fonts/`
};

interface InvoiceFilePreviewProps {
    label?: string;
    file?: IInvoiceFile;
    reviewWindowWidth?: number;
}

const dataTypes = ["application/pdf"];
const extensions = [".pdf"];

const InvoiceFilePreviewBase = (props: InvoiceFilePreviewProps) => {
    const { t } = useTranslation();
    const { invoiceStore, uiStore, invoiceFilePreviewStore } = useContext(RootStoreContext);
    const { file, reviewWindowWidth } = props;

    const invoiceFilePreviewRef = useRef<HTMLDivElement>(null);

    const [numPages, setNumPages] = useState<number>(0);
    const [isResizing, setIsResizing] = useState<boolean>(false);
    const [debouncedWidth, setDebouncedWidth] = useState<number | undefined>(reviewWindowWidth);

    useEffect(() => {
        const invoiceFormPreviewElement = invoiceFilePreviewRef.current;

        const updateWidth = debounce((width: number) => {
            setDebouncedWidth(width);
            setIsResizing(false);
        }, 300);

        const resizeObserver = new ResizeObserver((entries) => {
            for (let entry of entries) {
                if (entry.target === invoiceFormPreviewElement) {
                    const newWidth = entry.contentRect.width - 20;
                    uiStore.setInvoiceFilePreviewWidth(newWidth);
                    setIsResizing(true);
                    updateWidth(newWidth);
                }
            }
        });

        if (invoiceFormPreviewElement) {
            resizeObserver.observe(invoiceFormPreviewElement);
        }

        return () => {
            if (invoiceFormPreviewElement) {
                resizeObserver.unobserve(invoiceFormPreviewElement);
            }
            updateWidth.cancel();
        };
    }, [uiStore]);

    const onDeleteInvoiceFile = useCallback(async () => {
        await invoiceFilePreviewStore.handleDeleteInvoiceFile(file);
    }, [file, invoiceFilePreviewStore]);

    const onLoadError = useCallback((error: any) => {
        console.error("Fehler beim Laden der Datei: ", error.message);
    }, []);

    const onDocumentLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
        setNumPages(numPages);
    }, []);

    const overrideExistingDataModal = (
        <UI.Modal
            title={t("screens.kredi_flow.override_existing_data_title").toString()}
            size="lg"
            show={invoiceFilePreviewStore.displayOverrideExistingDataModal}
            onClose={invoiceFilePreviewStore.toggleDisplayOverrideExistingDataModal}
            centered={true}
        >
            <>
                <UI.Row className={"mt-2"}>
                    <UI.Col sm={12}>
                        <div className="mt-2 mb-4">{t("screens.kredi_flow.override_existing_data")}</div>
                    </UI.Col>
                </UI.Row>
                <UI.Row>
                    <UI.Col>
                        <UI.Button
                            label={t("labels.no").toString()}
                            className={`mb-2 ${styles.ModalButton}`}
                            onClick={invoiceFilePreviewStore.handleClickOverrideExistingDataNo}
                        />
                    </UI.Col>
                    <UI.Col>
                        <UI.Button
                            label={t("labels.yes").toString()}
                            className={`mb-2 ml-auto ${styles.ModalButton}`}
                            onClick={invoiceFilePreviewStore.handleClickOverrideExistingDataYes}
                        />
                    </UI.Col>
                </UI.Row>
            </>
        </UI.Modal>
    );

    const downloadInvoiceFile = useCallback(() => {
        if (file && file.fileId) {
            invoiceFilePreviewStore.downloadFile(file.fileId);
        }
    }, [file, invoiceFilePreviewStore]);

    const onSelectNewAttachmentFile = useCallback(
        (newAttachmentFile: IFile) => {
            invoiceStore.currentInvoice?.updateProperty(INVOICE_PROPERTY.INVOICE_ATTACHMENT_FILES, newAttachmentFile);
        },
        [invoiceStore.currentInvoice]
    );

    const onDeleteAttachmentFile = useCallback(
        async (fileId: string) => {
            await invoiceStore.currentInvoice?.deleteAttachmentFile(fileId);
        },
        [invoiceStore.currentInvoice]
    );

    const invoiceAttachmentFiles = (
        <div className={`p-3 ${styles.InvoiceAttachmentFilesContainer} ${file?.url ? styles.FileSelected : ""}`}>
            <div id="invoice-attchment-files-list">
                <FileSelectorContainer
                    style={uiStore.currentScreenSize <= SCREEN_SIZE.MOBILE_L ? { justifyContent: "end" } : {}}
                >
                    {/* Currently selected / loaded InvoiceAttachmentFilesFileSelectorItem to add new attachments */}
                    {invoiceStore.currentInvoice?.invoiceAttachmentFiles?.map((attachmentFile) => (
                        <FileSelectorItem
                            key={attachmentFile.fileId}
                            label={attachmentFile.name ?? "-"}
                            fileType={FILE_TYPE.INVOICE_ATTACHMENT}
                            file={attachmentFile}
                            onDeleteFilePropFunc={onDeleteAttachmentFile}
                            canDelete={invoiceStore.isEditing ? true : false}
                        />
                    ))}

                    {/* FileSelectorItem to add new attachments */}
                    {invoiceStore.isEditing && (
                        <FileSelectorItem
                            key={"add-new-invoice-attachment-file"}
                            label={"Weitere Dokumente"}
                            fileType={FILE_TYPE.INVOICE_ATTACHMENT}
                            file={undefined}
                            onSelectNewFilePropFunc={onSelectNewAttachmentFile}
                        />
                    )}
                </FileSelectorContainer>
            </div>
        </div>
    );

    return (
        <>
            {overrideExistingDataModal}
            <div
                ref={invoiceFilePreviewRef}
                className={styles.InvoiceFilePreviewContainer}
                id="invoice-file-preview-container"
            >
                <div className={styles.InvoicePreviewDropzone}>
                    {invoiceFilePreviewStore.isLoadingPDFFile ? (
                        <div className={`${styles.InvoicePreviewDropzoneContainer}`}>
                            <div>{t("screens.kredi_flow.document_is_being_analysed")}</div>
                            <UI.RotatingSpinner noLogo={true} size={25} />
                        </div>
                    ) : !file?.url &&
                      !invoiceFilePreviewStore.isLoadingPDFFile &&
                      !invoiceFilePreviewStore.isFileDeleting ? (
                        <>
                            <div className={styles.InvoicePreviewDropzoneContainer}>
                                <div>{t("screens.kredi_flow.add_document")}</div>
                                <input
                                    id={styles.InvoicePreviewInput}
                                    className={invoiceStore.isEditing ? "" : styles.NotAllowed}
                                    type="file"
                                    onChange={invoiceFilePreviewStore.handleFileInputChange({
                                        displayOverrideExistingDataModal: invoiceStore.currentInvoice?.isNewInvoice
                                            ? false
                                            : true,
                                        overrideData: true
                                    })}
                                    accept={dataTypes.concat(extensions).join(",")}
                                    disabled={!invoiceStore.isEditing}
                                />
                                <UI.Icon style={{ marginTop: 20 }} color="var(--grey)" icon={UI.SVGIcon.Documents} />
                            </div>
                            {invoiceAttachmentFiles}
                        </>
                    ) : null}
                </div>
                {file?.url && !invoiceFilePreviewStore.isLoadingPDFFile && !invoiceFilePreviewStore.isFileDeleting && (
                    <>
                        <div
                            className={styles.InvoicePreviewPdfContainer}
                            style={
                                uiStore.currentScreenSize > SCREEN_SIZE.MOBILE_L
                                    ? { overflowY: "scroll", height: uiStore.invoiceFormHeight, cursor: "pointer" }
                                    : { overflowY: "scroll", height: "500px" }
                            }
                        >
                            {isResizing ? (
                                <div>
                                    <UI.RotatingSpinner noLogo={true} size={25} />
                                </div>
                            ) : (
                                <Document
                                    file={file.url}
                                    onLoadSuccess={onDocumentLoadSuccess}
                                    onLoadError={onLoadError}
                                    loading={""}
                                    error={""}
                                    options={pdfDocumentOptions}
                                >
                                    {Array.from(new Array(numPages), (el, index) => (
                                        <Page
                                            className="invoice-file-preview-page"
                                            width={debouncedWidth}
                                            key={`page_${index + 1}`}
                                            pageNumber={index + 1}
                                        />
                                    ))}
                                </Document>
                            )}
                            {invoiceAttachmentFiles}
                        </div>
                        {!invoiceStore.isEditing && (
                            <div>
                                <div className={styles.OpenFileIcon}>
                                    <a
                                        href={NetworkConfig.datafileUrl + file.fileId}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="d-flex"
                                    >
                                        <UI.Icon color="var(--primary)" size={25} icon={UI.SVGIcon.ExternalLink} />
                                    </a>
                                </div>
                                <div className={styles.DownloadFileIcon}>
                                    <UI.Icon
                                        color="var(--primary)"
                                        size={30}
                                        icon={UI.SVGIcon.DownloadCloud}
                                        onClick={downloadInvoiceFile}
                                    />
                                </div>
                            </div>
                        )}
                        {invoiceStore.isEditing && invoiceStore.currentInvoice?.canDeleteDocument && (
                            <div className={styles.FileDeleteIcon}>
                                <UI.Icon onClick={onDeleteInvoiceFile} color="var(--grey)" icon={UI.SVGIcon.Trash} />
                            </div>
                        )}
                    </>
                )}
                {/* {invoiceAttachmentFiles} */}
            </div>
        </>
    );
};

export const InvoiceFilePreview = Sentry.withProfiler(observer(InvoiceFilePreviewBase));
